<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import {
  AlertCircleIcon,
  ArrowLeftIcon,
  BookOpen01Icon,
  DBtn,
  DCard,
  DLoading,
  FileCode01Icon
} from 'front-library'
import {
  BankTemplateDocumentation,
  getTemplateDocumentation
} from '@/app/core/services/bank-templates'

const router = useRouter()
const { t } = useI18n()

const documentation = ref<BankTemplateDocumentation | null>(null)
const loading = ref(false)
const error = ref('')

const htmlEscape: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;'
}

const escapeHtml = (value: string): string =>
  value.replace(/[&<>"']/g, character => htmlEscape[character] ?? character)

type DocumentationLinkAnchors = Record<string, string>

const normalizeDocumentationLink = (value: string): string =>
  value
    .replace(/^\.?\//, '')
    .replace(/#.*$/, '')
    .trim()
    .toLowerCase()

const resolveMarkdownLink = (
  url: string,
  anchors: DocumentationLinkAnchors
): { href: string; external: boolean } => {
  if (url.startsWith('http')) return { href: url, external: true }
  if (url.startsWith('#')) return { href: url, external: false }
  const normalized = normalizeDocumentationLink(url)
  const basename = normalized.split('/').pop() ?? normalized
  const anchor = anchors[normalized] ?? anchors[basename]
  return { href: anchor ? `#${anchor}` : '#', external: false }
}

const renderInlineMarkdown = (
  value: string,
  anchors: DocumentationLinkAnchors = {}
): string =>
  escapeHtml(value)
    .replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      (_match, label: string, url: string) => {
        const link = resolveMarkdownLink(url, anchors)
        const externalAttrs = link.external
          ? ' target="_blank" rel="noreferrer"'
          : ''
        return `<a href="${link.href}"${externalAttrs}>${label}</a>`
      }
    )
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')

const slugifyDocumentId = (value: string, index: number): string => {
  const slug = value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
  return slug || `section-${index + 1}`
}

const splitMarkdownTableRow = (line: string): string[] =>
  line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map(cell => cell.trim())

const isMarkdownTableSeparator = (line: string): boolean =>
  /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line)

const renderCodeBlock = (content: string, language = ''): string =>
  `<pre><code${
    language ? ` class="language-${escapeHtml(language)}"` : ''
  }>${escapeHtml(content.trim())}</code></pre>`

const renderMarkdown = (
  markdown: string,
  anchors: DocumentationLinkAnchors = {}
): string => {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n')
  const html: string[] = []
  const paragraph: string[] = []
  let listOpen = false

  const flushParagraph = () => {
    if (paragraph.length === 0) return
    html.push(`<p>${renderInlineMarkdown(paragraph.join(' '), anchors)}</p>`)
    paragraph.length = 0
  }

  const closeList = () => {
    if (!listOpen) return
    html.push('</ul>')
    listOpen = false
  }

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index]
    const trimmed = line.trim()

    if (trimmed.startsWith('```')) {
      flushParagraph()
      closeList()
      const language = trimmed.replace(/^```/, '').trim()
      const codeLines: string[] = []
      index += 1
      while (index < lines.length && !lines[index].trim().startsWith('```')) {
        codeLines.push(lines[index])
        index += 1
      }
      html.push(renderCodeBlock(codeLines.join('\n'), language))
      continue
    }

    if (
      trimmed.includes('|') &&
      index + 1 < lines.length &&
      isMarkdownTableSeparator(lines[index + 1])
    ) {
      flushParagraph()
      closeList()
      const headers = splitMarkdownTableRow(trimmed)
      const rows: string[][] = []
      index += 2
      while (index < lines.length && lines[index].trim().includes('|')) {
        rows.push(splitMarkdownTableRow(lines[index]))
        index += 1
      }
      index -= 1
      html.push(
        `<div class="documentation-table"><table><thead><tr>${headers
          .map(header => `<th>${renderInlineMarkdown(header, anchors)}</th>`)
          .join('')}</tr></thead><tbody>${rows
          .map(
            row =>
              `<tr>${row
                .map(cell => `<td>${renderInlineMarkdown(cell, anchors)}</td>`)
                .join('')}</tr>`
          )
          .join('')}</tbody></table></div>`
      )
      continue
    }

    const heading = trimmed.match(/^(#{1,4})\s+(.+)$/)
    if (heading) {
      flushParagraph()
      closeList()
      const level = Math.min(heading[1].length + 1, 5)
      html.push(
        `<h${level}>${renderInlineMarkdown(heading[2], anchors)}</h${level}>`
      )
      continue
    }

    const listItem = trimmed.match(/^-\s+(.+)$/)
    if (listItem) {
      flushParagraph()
      if (!listOpen) {
        html.push('<ul>')
        listOpen = true
      }
      html.push(`<li>${renderInlineMarkdown(listItem[1], anchors)}</li>`)
      continue
    }

    if (!trimmed) {
      flushParagraph()
      closeList()
      continue
    }

    paragraph.push(trimmed)
  }

  flushParagraph()
  closeList()
  return html.join('\n')
}

const documentationLinkAnchors = computed<DocumentationLinkAnchors>(() => {
  const anchors: DocumentationLinkAnchors = {}
  documentation.value?.sections.forEach((section, index) => {
    const anchor = `section-${slugifyDocumentId(section.title, index)}`
    const fileName = normalizeDocumentationLink(section.fileName)
    const basename = fileName.split('/').pop() ?? fileName
    anchors[fileName] = anchor
    anchors[basename] = anchor
  })
  documentation.value?.examples.forEach((example, index) => {
    const anchor = `example-${slugifyDocumentId(example.title, index)}`
    const fileName = normalizeDocumentationLink(example.fileName)
    const basename = fileName.split('/').pop() ?? fileName
    anchors[fileName] = anchor
    anchors[basename] = anchor
  })
  return anchors
})

const sectionCards = computed(
  () =>
    documentation.value?.sections.map((section, index) => ({
      ...section,
      anchor: `section-${slugifyDocumentId(section.title, index)}`,
      html: renderMarkdown(section.content, documentationLinkAnchors.value)
    })) ?? []
)

const exampleCards = computed(
  () =>
    documentation.value?.examples.map((example, index) => ({
      ...example,
      anchor: `example-${slugifyDocumentId(example.title, index)}`,
      html: renderCodeBlock(example.content, 'yaml')
    })) ?? []
)

const navigationItems = computed(() => [
  ...sectionCards.value.map(section => ({
    anchor: section.anchor,
    title: section.title
  })),
  ...exampleCards.value.map(example => ({
    anchor: example.anchor,
    title: example.title
  }))
])

const loadDocumentation = async () => {
  loading.value = true
  error.value = ''
  try {
    documentation.value = await getTemplateDocumentation()
  } catch (loadError: any) {
    error.value = loadError?.message ?? String(loadError)
  } finally {
    loading.value = false
  }
}

const goBack = () => {
  void router.push('/bank-templates')
}

onMounted(() => {
  void loadDocumentation()
})
</script>

<template>
  <div class="h-full px-5 pb-5 overflow-auto scroll bg-gray-50">
    <DLoading
      v-show="loading"
      :message="t('bankTemplates.documentation.loading')" />

    <div class="bg-gray-50 pt-responsive pb-4">
      <div class="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p class="text-3xl font-semibold text-shadow text-gray-900">
            {{ documentation?.title ?? t('bankTemplates.documentation.title') }}
          </p>
          <p class="text-gray-500 text-shadow mt-1 max-w-3xl">
            {{ t('bankTemplates.documentation.subtitle') }}
          </p>
        </div>
        <DBtn
          type="button"
          color="secondary"
          class="px-4 py-2.5 font-medium flex items-center gap-2"
          @click.prevent="goBack">
          <ArrowLeftIcon class="w-4 h-4" />
          {{ t('bankTemplates.studio.actions.back') }}
        </DBtn>
      </div>
    </div>

    <div
      v-if="error"
      class="flex items-start gap-2 bg-red-50 border border-red-100 rounded-lg px-4 py-3 text-sm text-red-600">
      <AlertCircleIcon class="w-4 h-4 shrink-0 mt-0.5" />
      <span>{{ error }}</span>
    </div>

    <div
      v-else-if="documentation"
      class="grid grid-cols-1 xl:grid-cols-[260px_minmax(0,1fr)] gap-5">
      <aside class="xl:sticky xl:top-4 self-start">
        <DCard>
          <div class="p-4">
            <p class="text-xs font-semibold uppercase text-gray-400 mb-3">
              {{ t('bankTemplates.documentation.contents') }}
            </p>
            <nav class="flex flex-col gap-1">
              <a
                v-for="item in navigationItems"
                :key="item.anchor"
                :href="`#${item.anchor}`"
                class="text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md px-2 py-1.5">
                {{ item.title }}
              </a>
            </nav>
          </div>
        </DCard>
      </aside>

      <main class="min-w-0 flex flex-col gap-5">
        <DCard v-for="section in sectionCards" :key="section.anchor">
          <article :id="section.anchor" class="documentation-card">
            <div class="flex items-center gap-2 mb-1">
              <BookOpen01Icon class="w-5 h-5 text-blue-500" />
              <h2 class="text-xl font-semibold text-gray-900">
                {{ section.title }}
              </h2>
            </div>
            <p class="text-xs text-gray-400 mb-4">
              {{ section.fileName }}
            </p>
            <div class="documentation-body" v-html="section.html" />
          </article>
        </DCard>

        <DCard v-for="example in exampleCards" :key="example.anchor">
          <article :id="example.anchor" class="documentation-card">
            <div class="flex items-center gap-2 mb-1">
              <FileCode01Icon class="w-5 h-5 text-blue-500" />
              <h2 class="text-xl font-semibold text-gray-900">
                {{ example.title }}
              </h2>
            </div>
            <p class="text-xs text-gray-400 mb-4">
              {{ example.fileName }}
            </p>
            <div class="documentation-body" v-html="example.html" />
          </article>
        </DCard>
      </main>
    </div>
  </div>
</template>

<style scoped>
.documentation-card {
  padding: 1.25rem;
  scroll-margin-top: 1rem;
}

.documentation-body :deep(h2) {
  margin: 1.5rem 0 0.5rem;
  color: #111827;
  font-size: 1.05rem;
  font-weight: 700;
}

.documentation-body :deep(h3),
.documentation-body :deep(h4),
.documentation-body :deep(h5) {
  margin: 1.25rem 0 0.5rem;
  color: #111827;
  font-size: 0.95rem;
  font-weight: 700;
}

.documentation-body :deep(p),
.documentation-body :deep(li) {
  color: #374151;
  font-size: 0.875rem;
  line-height: 1.7;
}

.documentation-body :deep(p) {
  margin: 0.75rem 0;
}

.documentation-body :deep(ul) {
  margin: 0.75rem 0 1rem 1.25rem;
  padding: 0;
}

.documentation-body :deep(a) {
  color: #2563eb;
  font-weight: 600;
}

.documentation-body :deep(code) {
  padding: 0.125rem 0.35rem;
  border-radius: 0.375rem;
  background: #eef2ff;
  color: #1f2937;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', monospace;
  font-size: 0.85em;
}

.documentation-body :deep(pre) {
  margin: 1rem 0;
  padding: 1rem;
  overflow: auto;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  background: #111827;
  color: #f9fafb;
  line-height: 1.6;
}

.documentation-body :deep(pre code) {
  padding: 0;
  background: transparent;
  color: inherit;
  font-size: 0.8125rem;
}

.documentation-body :deep(.documentation-table) {
  margin: 1rem 0;
  overflow: auto;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
}

.documentation-body :deep(table) {
  width: 100%;
  min-width: 720px;
  border-collapse: collapse;
  font-size: 0.8125rem;
}

.documentation-body :deep(th) {
  background: #f9fafb;
  color: #374151;
  font-weight: 700;
  text-align: left;
}

.documentation-body :deep(th),
.documentation-body :deep(td) {
  padding: 0.65rem 0.75rem;
  border-bottom: 1px solid #e5e7eb;
  vertical-align: top;
}

.documentation-body :deep(tr:last-child td) {
  border-bottom: 0;
}
</style>
